import { getItemDB, setItemDB } from "../../components/commons";
import chroma from "chroma-js";

import { COLOR_THEMES, DEFAULT_COLOR_THEME } from "../../components/forge/colorThemes";

/** Global for the user, not scoped per project. */
export const CONFIG_KEY = "af.dashboards";

/**
 * Widths are spans of a 12 column grid, except the first: a square is as wide
 * as it is tall, which no column count can promise, so it carries its width in
 * pixels and takes two columns of the flow to sit in.
 */
export const SIZES = [
  { span: 2, label: "□", square: true, title: "Square" },
  { span: 3, label: "¼" },
  { span: 4, label: "⅓" },
  { span: 6, label: "½" },
  { span: 12, label: "Full" },
];

/**
 * Heights are spans of the grid's rows, the same way widths are spans of its
 * columns. One row is what every widget has had until now.
 */
export const ROWS = [1, 2, 3, 4, 5, 6].map((rows) => ({ rows, label: String(rows) }));

/** The row the grid is built on; the locked height is measured against it. */
export const ROW_HEIGHT = 130;

/** The gap between cards, which a square spanning several rows has to include. */
export const GRID_GAP = 18;

/** The side of a square that many rows tall. */
export const squareSide = (rows = 1) => rows * ROW_HEIGHT + (rows - 1) * GRID_GAP;

export const isSquare = (span) => Boolean(SIZES.find((s) => s.span === span)?.square);

/* ---------------------------------------------------------------------------
   The canvas.

   A board used to be a twelve column grid: a widget carried a `span` and a
   `rows` count and the browser decided where it landed. It carries a box now -
   `x`, `y`, `w`, `h` - and sits exactly where it was put, overlaps and holes
   included.

   Across is stored as a fraction of the board's width and down in pixels. That
   split is deliberate: a card dragged to half the board stays half the board
   when the window narrows, while a card made 300px tall stays 300px tall -
   height has nothing to scale against.
--------------------------------------------------------------------------- */

/* A card may be dragged to any size at all. The only floor left is the one
   that keeps it recoverable: below this the grips have nothing to grab and the
   card could not be dragged back out again. What a widget does with a size too
   small for its content is the widget's business - it clips. */
export const MIN_W_PX = 48;
export const MIN_H_PX = 48;

/**
 * What a widget needs before its own content starts being cut off. No longer a
 * floor on resizing - a card can be dragged under it - but still what a board
 * is laid out at, so a migrated dashboard and a freshly added widget come up
 * showing all of themselves.
 *
 * They are also the floors the board is fitted to the window with (fitBoard):
 * a card is never squeezed under its number here, so the number has to be
 * what the card's content really takes.
 */
export const MIN_H_BY_WIDGET = {
  "scan-activity": 180,
  "scans-by-os": 195,
  "end-of-life": 200,
  "forge-runs-per-day": 180,
  /* the ids are the widget's own, not its label: "Forge org spend" is
     forge-spend and "Forge counters" is forge-kpis, which is why the first
     pass of this table let both of them squeeze under their own content */
  "forge-spend": 200,
  "forge-kpis": 190,
  "forge-outcomes": 150,
  /* the console instruments: each one stacks a figure, a graphic and a list,
     and clips into nonsense under these */
  "forge-run-status": 300,
  /* the estate instruments from the third sample: a ring with a list beside
     it, meters over a host grid, a unit chart, a bubble field */
  "estate-progress": 300,
  "hosts-by-os-list": 260,
  "vulnerability-state": 280,
  "scans-by-os-pick": 260,
  "end-of-life-units": 260,
  "assessment-bubbles": 330,
  "forge-orbit": 340,
  "forge-campaign-donut": 260,
  "forge-spend-gauge": 345,
  "forge-outcomes-ring": 350,
  "forge-runs-chart": 300,
  "forge-pulse": 96,
  /* a status per row, up to seven */
  "forge-campaign-status": 220,
  /* the eight newest runs */
  "forge-last-runs": 260,
  "forge-queue-spend": 270,
  "forge-runs-trend": 270,
};

/** The height a widget wants: the nominal row, unless it asks for more. */
export const minHeightOf = (id) => Math.max(ROW_HEIGHT, MIN_H_BY_WIDGET[id] || 0);

/* ---------------------------------------------------------------------------
   What auto arrange sizes a card by: its CONTENT, not the width it happens to
   have. Arranging from the current widths kept whatever proportions a board
   had drifted into - four counter tiles given a third of the row, a donut with
   a fourteen-line legend squeezed to a sliver beside it.

   ARRANGE_SPAN is a card's fair share of a row, on the twelve-column scale:
   figures and short lists take little, charts and anything with a legend take
   more. MIN_W_BY_WIDGET is the width under which a card stops working - a
   legend cut off, assessment cards overlapping - and is never arranged under.
--------------------------------------------------------------------------- */
const ARRANGE_SPAN = {
  "in-your-estate": 3,
  "scan-estate": 3,
  "scan-coverage": 3,
  "scan-activity": 4,
  "estate-progress": 4,
  "scans-by-os": 4,
  "scans-by-os-pick": 3,
  "assessment-summary": 5,
  "assessment-bubbles": 6,
  "ips-not-scanned": 5,
  "end-of-life": 3,
  "end-of-life-units": 3,
  "vulnerability-state": 3,
  "hosts-by-os": 4,
  "hosts-by-os-list": 4,
  "hosts-by-java": 4,
  "forge-kpis": 4,
  "forge-run-status": 3,
  "forge-pulse": 3,
  "forge-outcomes": 3,
  "forge-outcomes-ring": 4,
  "forge-orbit": 4,
  "forge-runs-per-day": 4,
  "forge-runs-chart": 3,
  "forge-spend": 3,
  "forge-spend-gauge": 3,
  "forge-campaign-status": 3,
  "forge-campaign-donut": 3,
  "forge-last-runs": 4,
  "forge-queue-spend": 6,
  "forge-runs-trend": 5,
};

export const MIN_W_BY_WIDGET = {
  "scan-activity": 300,
  "estate-progress": 420,
  "scans-by-os": 300,
  "assessment-summary": 420,
  "assessment-bubbles": 460,
  "ips-not-scanned": 380,
  "hosts-by-os": 380,
  "hosts-by-os-list": 360,
  "hosts-by-java": 380,
  "forge-kpis": 420,
  "forge-outcomes-ring": 380,
  "forge-orbit": 420,
  "forge-runs-per-day": 300,
  "forge-runs-chart": 260,
  "forge-last-runs": 320,
  "forge-queue-spend": 560,
  "forge-runs-trend": 460,
};

/** The width a card can be read at; the floor for anything not listed. */
export const READABLE_W = 240;

export const arrangeSpanOf = (id) =>
  ARRANGE_SPAN[id] ?? Math.min(6, WIDGETS.find((w) => w.id === id)?.span ?? 3);

export const minWidthOf = (id) => MIN_W_BY_WIDGET[id] ?? READABLE_W;

/** Room left under the lowest card, so there is somewhere to drag to. */
export const CANVAS_PAD = 40;

/** The height a card of this many nominal rows gets. */
export const rowsToHeight = (rows = 1) => rows * ROW_HEIGHT + (rows - 1) * GRID_GAP;

/** Has this widget been through the migration below? */
export const hasBox = (w) =>
  Number.isFinite(w?.x) && Number.isFinite(w?.y) && Number.isFinite(w?.w) && Number.isFinite(w?.h);

/**
 * The old layout, as a set of boxes.
 *
 * It walks the widgets the way the grid did - filling a row of twelve columns
 * and wrapping when the next one does not fit - so a board that has never been
 * dragged comes out looking exactly as it did. Widths come back as fractions,
 * which is why the board's measured width has to be passed in.
 *
 * @param {Array} widgets the stored widgets, in order
 * @param {number} boardWidth the canvas width in pixels
 */
export function toCanvas(widgets = [], boardWidth = 0) {
  if (!boardWidth) return widgets;

  const colWidth = (boardWidth - GRID_GAP * 11) / 12;
  const spanToPx = (span) => span * colWidth + (span - 1) * GRID_GAP;

  let used = 0;      /* columns taken on the row being filled */
  let top = 0;       /* where that row starts */
  let rowHeight = 0; /* the tallest card on it */

  return widgets.map((widget) => {
    if (hasBox(widget)) return widget;

    const rows = Math.max(1, Number(widget.rows) || 1);
    const square = isSquare(widget.span);
    const span = Math.min(12, Math.max(1, Number(widget.span) || 4));
    /* the floor is part of the height: a card the render will hold at 195 has
       to be laid out as 195 here, or the row under it starts where the card
       has not finished */
    const height = Math.max(minHeightOf(widget.id), square ? squareSide(rows) : rowsToHeight(rows));
    const width = square ? squareSide(rows) : spanToPx(span);

    if (used + span > 12) {
      top += rowHeight + GRID_GAP;
      used = 0;
      rowHeight = 0;
    }

    const left = used * (colWidth + GRID_GAP);
    used += span;
    rowHeight = Math.max(rowHeight, height);

    return {
      ...widget,
      x: left / boardWidth,
      w: Math.min(1, width / boardWidth),
      y: top,
      h: height,
    };
  });
}

/**
 * Makes room for a card whose size was just changed with the size controls,
 * so it does not run over its neighbours.
 *
 * Only positions change - every other card keeps the size it was given.
 *   - push: in one sweep down the page, a card that now overlaps a card
 *     above it moves down just clear of it (with the gutter) - or, if it sits
 *     beside the resized card on the same line, right, when that keeps it on
 *     the board. Order down the page is kept.
 *   - pull: cards that sat under the card's old box, and any card that was
 *     pushed, move back up as far as the cards above them allow - so a card
 *     made smaller, or put back, leaves no hole under it.
 * Cards nowhere near the change are not touched.
 *
 * @param {Array} widgets the board, with `id` already at its new size
 * @param {string} id the card that was resized
 * @param {{x:number,y:number,w:number,h:number}} before its box before
 * @param {number} boardWidth the canvas width in pixels
 */
export function makeRoom(widgets = [], id, before, boardWidth = 0) {
  if (!boardWidth || !widgets.some((w) => w.id === id && hasBox(w))) return widgets;

  /* in pixels, where "clear of it" is measured */
  const boxes = new Map(widgets.filter(hasBox).map((w) => [w.id, {
    x: w.x * boardWidth, y: w.y, w: w.w * boardWidth, h: w.h,
  }]));
  const across = (a, b) => a.x < b.x + b.w - 1 && b.x < a.x + a.w - 1;
  const overlap = (a, b) => across(a, b) && a.y < b.y + b.h - 1 && b.y < a.y + a.h - 1;
  const moved = new Set();

  /* Push, as one sweep down the page: the resized card is placed first, then
     every other card in reading order, each moved down only as far as it
     takes to clear the cards already placed. So no card can overtake one that
     was above it, and nothing is left overlapping. A card BESIDE the resized
     one - level with its top, to its right - steps right instead when that
     keeps it on the board, rather than dropping a whole row. */
  const resized = boxes.get(id);
  const placed = [resized];
  const others = [...boxes.keys()]
    .filter((oid) => oid !== id)
    .sort((a, b) => boxes.get(a).y - boxes.get(b).y || boxes.get(a).x - boxes.get(b).x);
  for (const oid of others) {
    const o = boxes.get(oid);
    const beside = Math.abs(o.y - resized.y) <= 1 && o.x >= resized.x;
    const rightX = resized.x + resized.w + GRID_GAP;
    if (beside && overlap(resized, o) && rightX + o.w <= boardWidth + 1) {
      o.x = rightX;
      moved.add(oid);
    }
    for (let guard = placed.length + 1; guard > 0; guard--) {
      const hit = placed.find((q) => overlap(q, o));
      if (!hit) break;
      o.y = hit.y + hit.h + GRID_GAP;
      moved.add(oid);
    }
    placed.push(o);
  }

  /* pull back up: what was under the old box, and whatever moved */
  const old = before && Number.isFinite(before.y)
    ? { x: before.x * boardWidth, y: before.y, w: before.w * boardWidth, h: before.h }
    : null;
  const pullable = new Set(moved);
  for (const [oid, o] of boxes) {
    if (oid !== id && old && across(old, o) && o.y >= old.y + old.h - 1) pullable.add(oid);
  }
  /* top to bottom, so a card pulled up makes room for the one under it;
     anything under a pulled card in its columns may follow it */
  const order = [...boxes.keys()].sort((a, b) => boxes.get(a).y - boxes.get(b).y);
  for (const oid of order) {
    if (!pullable.has(oid)) continue;
    const o = boxes.get(oid);
    let top = 0;
    for (const [pid, p] of boxes) {
      if (pid === oid || !across(p, o) || p.y >= o.y) continue;
      top = Math.max(top, p.y + p.h + GRID_GAP);
    }
    if (top < o.y) {
      o.y = top;
      for (const [qid, q] of boxes) if (qid !== oid && across(o, q) && q.y > o.y) pullable.add(qid);
    }
  }

  return widgets.map((w) => {
    const b = boxes.get(w.id);
    if (!b || w.id === id) return w;
    const x = b.x / boardWidth;
    const y = Math.round(b.y);
    return Math.abs(x - w.x) < 1e-6 && y === Math.round(w.y) ? w : { ...w, x, y };
  });
}

/**
 * A box for a widget being added to a board that is already a canvas: under
 * everything else, at the width the widget asks for.
 */
export function boxForNew(widgets = [], span = 4, boardWidth = 0, id = "") {
  const bottom = widgets.reduce((low, w) => (hasBox(w) ? Math.max(low, w.y + w.h) : low), 0);
  const colWidth = boardWidth ? (boardWidth - GRID_GAP * 11) / 12 : 0;
  const width = colWidth ? (span * colWidth + (span - 1) * GRID_GAP) / boardWidth : span / 12;
  return {
    x: 0,
    y: bottom ? bottom + GRID_GAP : 0,
    w: Math.min(1, width),
    h: Math.max(minHeightOf(id), rowsToHeight(1)),
  };
}

/**
 * Every widget the dashboard can show. `span` is the width it gets when it is
 * first placed; the layout stores whatever the user resizes it to.
 */
export const WIDGETS = [
  { id: "scan-estate", label: "Scan Estate", span: 4, group: "Estates" },

  { id: "in-your-estate", label: "In your estate", span: 4, group: "Estate", variant: "Counters" },
  { id: "estate-progress", label: "Modernization progress", span: 5, optional: true, group: "Estate", variant: "Progress ring" },

  { id: "scan-coverage", label: "Scan coverage", span: 4, group: "Scan coverage" },
  { id: "scan-activity", label: "Scan activity", span: 3, group: "Scan activity" },

  { id: "scans-by-os", label: "Scans by operating system", span: 3, group: "Scans by OS", variant: "Bars" },
  { id: "scans-by-os-pick", label: "Scans by platform", span: 4, optional: true, group: "Scans by OS", variant: "One platform" },

  { id: "assessment-summary", label: "Application assessment summary", span: 6, group: "Application assessments", variant: "Cards" },
  { id: "assessment-bubbles", label: "Application assessment map", span: 8, optional: true, group: "Application assessments", variant: "Risk map" },

  { id: "ips-not-scanned", label: "IPs not scanned", span: 6, group: "IPs not scanned" },

  { id: "end-of-life", label: "End of life software", span: 6, group: "End of life", variant: "Figures" },
  { id: "end-of-life-units", label: "End of life, by package", span: 4, optional: true, group: "End of life", variant: "Unit chart" },

  { id: "vulnerability-state", label: "Vulnerability state", span: 3, optional: true, group: "Vulnerabilities" },

  /* carried over from the old dashboard: on the add list, but not on the board
     a new user starts with */
  { id: "hosts-by-os", label: "Hosts by OS", span: 4, optional: true, group: "Hosts by OS", variant: "Donut" },
  { id: "hosts-by-os-list", label: "Hosts by OS, listed", span: 4, optional: true, group: "Hosts by OS", variant: "Donut and list" },
  { id: "hosts-by-java", label: "Hosts by Java version", span: 4, optional: true, group: "Hosts by Java" },

  /* Forge, drawn in this dashboard's own vocabulary rather than lifted from the
     Forge page with its stylesheet. Optional: they cost four calls to a service
     a tenant may not run, so they are added deliberately rather than seeded on
     everyone's board. The sample dashboard below carries the set. */
  { id: "forge-kpis", label: "Forge counters", span: 6, optional: true, forge: true, group: "Forge status", variant: "Counters" },
  { id: "forge-run-status", label: "Forge run status", span: 3, optional: true, forge: true, group: "Forge status", variant: "Status stack" },
  { id: "forge-pulse", label: "Forge pulse", span: 3, optional: true, forge: true, group: "Forge status", variant: "Pulse" },

  { id: "forge-outcomes", label: "Forge outcomes", span: 3, optional: true, forge: true, group: "Forge outcomes", variant: "Figure" },
  { id: "forge-outcomes-ring", label: "Forge outcomes ring", span: 4, optional: true, forge: true, group: "Forge outcomes", variant: "Ring" },
  { id: "forge-orbit", label: "Forge orbit", span: 5, optional: true, forge: true, group: "Forge outcomes", variant: "Orbit" },

  { id: "forge-runs-per-day", label: "Forge runs per day", span: 6, optional: true, forge: true, group: "Forge runs per day", variant: "Spark" },
  { id: "forge-runs-chart", label: "Forge runs chart", span: 6, optional: true, forge: true, group: "Forge runs per day", variant: "Stacked chart" },

  { id: "forge-spend", label: "Forge org spend", span: 3, optional: true, forge: true, group: "Forge spend", variant: "Figures" },
  { id: "forge-spend-gauge", label: "Forge spend gauge", span: 3, optional: true, forge: true, group: "Forge spend", variant: "Gauge" },

  { id: "forge-campaign-status", label: "Forge campaign status", span: 3, optional: true, forge: true, group: "Forge campaigns", variant: "Rows" },
  { id: "forge-campaign-donut", label: "Forge campaign donut", span: 4, optional: true, forge: true, group: "Forge campaigns", variant: "Donut" },

  { id: "forge-last-runs", label: "Forge last runs", span: 6, optional: true, forge: true, group: "Forge runs" },

  /* The Forge page's own two dashboard cards, as they are drawn there (its
     stylesheet and all) rather than redrawn in this board's vocabulary.
     `pageCard` keeps them out of the Forge page's "Mission Control" list,
     where they are offered already as the page's own. */
  { id: "forge-queue-spend", label: "Forge queue & spend", span: 6, optional: true, forge: true, pageCard: true, group: "Forge page cards" },
  { id: "forge-runs-trend", label: "Forge runs trend", span: 6, optional: true, forge: true, pageCard: true, group: "Forge page cards" },
];

/**
 * The add list, as groups.
 *
 * Several widgets are the same measurement drawn a different way - hosts by OS
 * as a donut or as a donut with its list beside it, Forge outcomes as a figure,
 * a ring or an orbit. They are one entry with types rather than three unrelated
 * rows, so the list reads as what the board can show rather than as everything
 * that has ever been built for it. Order is the catalogue's own, and a group is
 * its widgets in the order they appear above.
 */
export function widgetGroups() {
  const groups = [];
  const byName = new Map();
  for (const widget of WIDGETS) {
    /* Every Forge widget goes under the one Forge entry, whatever it measures:
       as their own groups they were five headings and thirteen rows strung
       through the list, for one service. The first one places the entry. */
    const name = widget.forge ? FORGE_GROUP : widget.group || widget.label;
    if (!byName.has(name)) {
      const group = { name, items: [], forge: Boolean(widget.forge) };
      byName.set(name, group);
      groups.push(group);
    }
    byName.get(name).items.push(widget);
  }
  return groups;
}

export const FORGE_GROUP = "Forge";

/** What a row in the add list is called: its type within a group, or its name. */
export const widgetRowLabel = (widget) => {
  /* under the Forge entry the prefix is the heading already: "Counters",
     "Org spend", "Campaign donut" */
  if (widget.forge) {
    const short = widget.label.replace(/^Forge\s+/i, "");
    return short.charAt(0).toUpperCase() + short.slice(1);
  }
  return widget.variant && (WIDGETS.filter((w) => (w.group || w.label) === (widget.group || widget.label)).length > 1)
    ? widget.variant
    : widget.label;
};

/** Every widget that reads from Forge - drawn by ForgeWidget, here and on the Forge page. */
export const FORGE_WIDGET_IDS = new Set(WIDGETS.filter((w) => w.forge).map((w) => w.id));

/** True once the board carries a widget that reads from Forge. */
export const usesForge = (widgets = []) => {
  const forge = new Set(WIDGETS.filter((w) => w.forge).map((w) => w.id));
  return widgets.some((w) => forge.has(w.id));
};

export const widgetLabel = (id) => WIDGETS.find((w) => w.id === id)?.label || id;

export const DEFAULT_DASHBOARD_ID = "modernization";

/**
 * A board to look at the Forge widgets on, not a board anyone starts on: it is
 * seeded beside Modernization and never made active, so it is there to open,
 * change and export without standing between a new user and their estate.
 */
export const FORGE_SAMPLE_ID = "forge-sample";

function forgeSample() {
  return {
    id: FORGE_SAMPLE_ID,
    name: "Forge (sample)",
    sample: true,
    widgets: [
      { id: "forge-kpis", span: 6 },
      { id: "forge-outcomes", span: 3 },
      { id: "forge-spend", span: 3 },
      { id: "forge-runs-per-day", span: 6 },
      { id: "forge-campaign-status", span: 3 },
      { id: "forge-last-runs", span: 6 },
    ],
  };
}

/**
 * Modernization, as exported from demo42 on 2026-09-21: fourteen cards, each
 * with the box it was arranged into - across as a fraction of the board, down
 * in pixels. Boxes rather than widths this time, because the board was laid
 * out by hand rather than packed into rows, and only the boxes carry that.
 */
const DEFAULT_LAYOUT = [
  { id: "scan-estate", span: 4, x: 0, y: 305, w: 0.19366, h: 160 },
  { id: "in-your-estate", span: 4, x: 0, y: 0, w: 0.19366, h: 287 },
  { id: "scan-coverage", span: 4, x: 0.52037, y: 0, w: 0.15611, h: 287 },
  { id: "scan-activity", span: 3, x: 0.20718, y: 0, w: 0.30207, h: 287 },
  { id: "scans-by-os", span: 3, x: 0.52037, y: 305, w: 0.27346, h: 253 },
  { id: "assessment-summary", span: 6, x: 0.65431, y: 716, w: 0.34569, h: 293 },
  { id: "ips-not-scanned", span: 6, x: 0.20718, y: 305, w: 0.30207, h: 393 },
  { id: "end-of-life", span: 6, x: 0.80495, y: 305, w: 0.19505, h: 253 },
  { id: "forge-kpis", span: 7, x: 0.65431, y: 576, w: 0.34569, h: 121 },
  { id: "forge-spend", span: 5, x: 0.52277, y: 577, w: 0.12042, h: 120 },
  { id: "forge-outcomes", span: 4, x: 0.3826, y: 715, w: 0.26059, h: 293 },
  { id: "forge-runs-per-day", span: 8, x: 0, y: 483, w: 0.19366, h: 215 },
  { id: "hosts-by-java", span: 6, x: 0.6876, y: 0, w: 0.3124, h: 287 },
  { id: "hosts-by-os", span: 6, x: 0, y: 715, w: 0.37148, h: 292 },
];

/**
 * The canvas height the layout above was arranged at. Its boxes are pixels, so
 * they only mean what they meant on the screen they came off; this is the
 * reference everything vertical in it is read against.
 */
export const DEFAULT_LAYOUT_HEIGHT =
  DEFAULT_LAYOUT.reduce((low, w) => Math.max(low, (w.y || 0) + (w.h || 0)), 0) + CANVAS_PAD;

/* How far the default may be squeezed before it is left alone to scroll: past
   this the cards are too short to read and a scrollbar is the better answer. */
const MIN_FIT = 0.5;

/**
 * The default board, at the height the screen actually gives it.
 *
 * The layout above was arranged on one particular window; opened on a shorter
 * one it hangs off the bottom, and the first thing a new user meets is a
 * scrollbar. Everything vertical - where a card starts and how tall it is - is
 * scaled by one factor, so the board keeps its proportions and its rows stay in
 * step. Widths are untouched: they are fractions of the board already.
 *
 * Only ever shrinks. A tall window leaves the layout as drawn rather than
 * stretching fourteen cards to fill it.
 *
 * @param {Array} widgets the boards's widgets, with boxes
 * @param {number} available the canvas's own height in the window
 * @param {number} reference the height the layout was arranged at
 */
export function fitToHeight(widgets = [], available = 0, reference = DEFAULT_LAYOUT_HEIGHT) {
  if (!available || !reference || available >= reference) return widgets;

  /* the room under the lowest card is a constant, not something to scale: take
     it off both sides or the board comes out that much too tall and still
     scrolls, by exactly the pad */
  const room = available - CANVAS_PAD;
  const content = reference - CANVAS_PAD;
  if (room <= 0 || content <= 0) return widgets;

  const factor = Math.max(MIN_FIT, room / content);
  if (factor > 0.995) return widgets;

  return widgets.map((widget) =>
    hasBox(widget)
      ? {
          ...widget,
          y: Math.round(widget.y * factor),
          h: Math.max(MIN_H_PX, Math.round(widget.h * factor)),
        }
      : widget,
  );
}

/**
 * The board, fitted to the height the window gives it - every time it is
 * shown, not once.
 *
 * Stored heights are pixels off whatever screen the board was arranged on, so
 * as stored it leaves a band of empty canvas on a tall window and hangs off -
 * or gets squeezed into clipping - on a short one. Here the page is cut at
 * every card's top and bottom edge into horizontal bands, and the bands that
 * have a card in them are stretched or shrunk to fill `room` exactly. The
 * gutters between cards (bands nobody is in) keep their size. So rows stay in
 * step, side-by-side cards stay level, and nothing overlaps that did not
 * already.
 *
 * Each card is then held at the height its content needs (`floorOf`): a band
 * under a card that would come out too short is grown back and pinned, and the
 * free bands share what is left. When the floors alone need more than the room
 * the board comes out taller than the window and scrolls - a scrollbar rather
 * than a cropped card.
 *
 * Widths are untouched: they are fractions of the board already.
 *
 * @param {Array} widgets the board's widgets, with boxes
 * @param {number} room the height the canvas has in the window
 * @param {function} floorOf (id) => the least height that widget can show at
 * @returns {{ boxes: Map<string, {top: number, height: number}>, height: number }}
 */
const SNAP_PX = 4;

export function fitBoard(widgets = [], room = 0, floorOf = minHeightOf) {
  const cards = widgets
    .filter(hasBox)
    .map((w) => ({ id: w.id, y: Math.max(0, w.y), b: Math.max(0, w.y) + Math.max(MIN_H_PX, w.h), floor: floorOf(w.id) }));
  const boxes = new Map();
  if (!cards.length || !(room > 0)) return { boxes, height: 0 };

  /* Edges a few pixels apart are one edge: a hand-arranged board has cards
     meant to be level at 576 and 577, and kept apart they would come out as two
     rows a pixel off each other. */
  const raw = [...new Set(cards.flatMap((c) => [c.y, c.b]))].sort((a, b) => a - b);
  const snap = new Map();
  const edges = [];
  for (const e of raw) {
    if (edges.length && e - edges[edges.length - 1] <= SNAP_PX) snap.set(e, edges[edges.length - 1]);
    else { edges.push(e); snap.set(e, e); }
  }
  const bands = edges.slice(0, -1).map((top, i) => ({ top, len: edges[i + 1] - top }));
  const index = new Map(edges.map((e, i) => [e, i]));
  for (const c of cards) {
    c.from = index.get(snap.get(c.y));
    c.to = Math.max(c.from + 1, index.get(snap.get(c.b)));
  }
  /* A gutter keeps its height: a band no card sits in, and a band that is the
     gap between one card's bottom and another's top - even where a taller card
     beside them spans it, which is what made it look like a row. A gap that an
     earlier squeeze left narrow is put back to the board's own. */
  const gutter = bands.map((band, i) =>
    band.len <= GRID_GAP * 1.5 &&
    cards.some((c) => c.to === i) &&
    cards.some((c) => c.from === i + 1));
  const covered = bands.map((_, i) => !gutter[i] && cards.some((c) => c.from <= i && i < c.to));
  bands.forEach((band, i) => { if (gutter[i]) band.len = GRID_GAP; });
  /* the space above the first card is not part of the board */
  const lead = edges[0];
  const fixed = lead + bands.reduce((sum, band, i) => sum + (covered[i] ? 0 : band.len), 0);
  const flexTotal = bands.reduce((sum, band, i) => sum + (covered[i] ? band.len : 0), 0);

  const len = bands.map((band) => band.len);
  const pinned = bands.map((_, i) => !covered[i]);
  const target = Math.max(0, room - fixed);

  /* Alternate: share the room out over the free bands, then grow back any card
     that came out under its floor and pin its bands. Pins only ever get added,
     so it settles within one pass per card. */
  for (let pass = 0; pass <= cards.length; pass += 1) {
    const pinnedSum = len.reduce((sum, l, i) => sum + (pinned[i] && covered[i] ? l : 0), 0);
    const freeOrig = bands.reduce((sum, band, i) => sum + (pinned[i] ? 0 : band.len), 0);
    if (freeOrig > 0) {
      const scale = Math.max(0, target - pinnedSum) / freeOrig;
      bands.forEach((band, i) => { if (!pinned[i]) len[i] = band.len * scale; });
    }

    let grew = false;
    for (const c of cards) {
      let have = 0;
      for (let i = c.from; i < c.to; i += 1) have += len[i];
      if (have >= c.floor - 0.5) continue;
      /* Grow the card's free bands, and only those, when they can cover it:
         its pinned ones are holding someone else's floor. When they cannot -
         a card level with a neighbour shares every band with it - its pinned
         bands grow too, which only ever gives that neighbour more room. */
      let pinnedPart = 0;
      let freeOrigOwn = 0;
      for (let i = c.from; i < c.to; i += 1) {
        if (pinned[i]) pinnedPart += len[i];
        else freeOrigOwn += bands[i].len;
      }
      if (freeOrigOwn > 0) {
        const need = c.floor - pinnedPart;
        for (let i = c.from; i < c.to; i += 1) {
          if (!pinned[i]) { len[i] = bands[i].len * (need / freeOrigOwn); pinned[i] = true; }
        }
      } else if (have > 0) {
        let own = 0;
        for (let i = c.from; i < c.to; i += 1) if (covered[i]) own += len[i];
        if (!(own > 0)) continue;
        const k = (c.floor - (have - own)) / own;
        for (let i = c.from; i < c.to; i += 1) if (covered[i]) len[i] *= k;
      } else {
        continue;
      }
      grew = true;
    }
    if (!grew) break;
  }
  if (!(flexTotal > 0)) return { boxes, height: 0 };

  /* the bands back into positions, rounded at the edges so neighbours meet */
  const at = [lead];
  len.forEach((l, i) => at.push(at[i] + l));
  const px = at.map(Math.round);
  for (const c of cards) boxes.set(c.id, { top: px[c.from], height: px[c.to] - px[c.from] });
  return { boxes, height: px[px.length - 1] };
}

/**
 * The board a reset goes back to: Modernization on its own. The Forge sample is
 * a seed, not a default - it is put out once for a look and stays deleted, so
 * `forgeSampleSeeded` rides along to stop the loader offering it again.
 */
function defaultConfig() {
  return {
    version: 1,
    activeId: DEFAULT_DASHBOARD_ID,
    forgeSampleSeeded: true,
    dashboards: [
      {
        id: DEFAULT_DASHBOARD_ID,
        name: "Modernization",
        /* copied, not shared: a board is edited in place, and the default must
           not come back changed the next time it is asked for */
        widgets: DEFAULT_LAYOUT.map((w) => ({ ...w })),
      },
    ],
  };
}

/**
 * Reads the saved layout, seeding it with the default Modernization dashboard
 * the first time. getItemDB resolves to {} rather than null for a missing key,
 * so the check is on the payload rather than on falsiness.
 */
export async function loadDashboardConfig() {
  try {
    const stored = await getItemDB(CONFIG_KEY);
    if (stored?.dashboards?.length) {
      /* Seeded once, for boards saved before the Forge widgets existed. Only
         when it is missing entirely: deleting it has to stick. */
      if (!stored.dashboards.some((d) => d.id === FORGE_SAMPLE_ID) && !stored.forgeSampleSeeded) {
        const seeded = {
          ...stored,
          forgeSampleSeeded: true,
          dashboards: [...stored.dashboards,
            //  forgeSample()
            ],
        };
        saveDashboardConfig(seeded);
        return seeded;
      }
      return stored;
    }
  } catch (e) {
    console.error("Dashboard: could not read the saved layout", e);
  }

  /* nothing stored at all: the default board, and the Forge sample beside it
     the one time, so a new estate has somewhere to see those widgets */
  const seeded = defaultConfig();
  seeded.dashboards = [
    ...seeded.dashboards,
    // forgeSample()
  ];
  saveDashboardConfig(seeded);
  return seeded;
}

/** Throws away every dashboard and returns the seeded Modernization default. */
export async function resetDashboardConfig() {
  const fresh = defaultConfig();
  await saveDashboardConfig(fresh);
  return fresh;
}

export async function saveDashboardConfig(config) {
  try {
    await setItemDB(CONFIG_KEY, config);
  } catch (e) {
    console.error("Dashboard: could not save the layout", e);
  }
}

/* ---------- transfer ---------- */

export const EXPORT_FORMAT = "appfactor.dashboards";
export const EXPORT_EXTENSION = ".adf";

/**
 * <subdomain>.appfactor.adf — the tenant the layout came from, so a file picked
 * out of a downloads folder still says where it belongs.
 */
export function exportFileName(hostname = window.location.hostname) {
  const [subdomain] = String(hostname || "").split(".");
  return `${subdomain || "local"}.appfactor${EXPORT_EXTENSION}`;
}

/* Boxes are compared to the pixel and to three decimals across: enough to tell
   a card that was dragged from one that was never touched, loose enough not to
   trip over the float the fraction comes back as.

   Down the page the comparison is against a factor rather than against the
   numbers themselves: a default board opened on a short screen is fitted to it
   (see fitToHeight), and a board that was only ever scaled to the window it
   was opened in has not been touched by anybody. The factor is taken from the
   first card and every other card has to agree with it, so a single card
   dragged taller still reads as an edit. */
const sameBox = (a, b, factor = 1) =>
  Math.round(a.y ?? 0) === Math.round((b.y ?? 0) * factor) &&
  Math.abs(Math.round(a.h ?? 0) - Math.round((b.h ?? 0) * factor)) <= 1 &&
  Math.abs((a.x ?? 0) - (b.x ?? 0)) < 0.001 &&
  Math.abs((a.w ?? 0) - (b.w ?? 0)) < 0.001;

const sameLayout = (a, b) => {
  if (
    a.name !== b.name ||
    (a.palette && a.palette !== DEFAULT_COLOR_THEME) ||
    a.widgets.length !== b.widgets.length
  ) return false;

  /* the one vertical factor the whole board would have been fitted by */
  const first = b.widgets[0];
  const factor = first?.h ? (a.widgets[0]?.h ?? 0) / first.h : 1;
  if (!(factor > 0) || factor > 1.001) return false;

  return a.widgets.every(
    (w, i) =>
      w.id === b.widgets[i].id &&
      w.span === b.widgets[i].span &&
      !w.title &&
      !w.noFrame &&
      !w.noTitle &&
      sameBox(w, b.widgets[i], factor),
  );
};

/** True while the Modernization dashboard is still exactly as it was seeded. */
export function isUntouchedDefault(dashboard) {
  if (dashboard?.id !== DEFAULT_DASHBOARD_ID) return false;
  return sameLayout(dashboard, defaultConfig().dashboards[0]);
}

/**
 * The file's contents. An untouched Modernization carries nothing the other end
 * does not already have, so it is left out.
 */
export function buildExport(config, hostname = window.location.hostname, dashboards) {
  return {
    format: EXPORT_FORMAT,
    version: 1,
    exportedAt: new Date().toISOString(),
    source: hostname,
    /* By default the boards worth carrying; the export panel passes the whole
       set instead, so it can offer every board and tick these ones. */
    dashboards: (dashboards || (config?.dashboards || []).filter((dash) => !isUntouchedDefault(dash)))
      .map((dash) => ({
        name: dash.name,
        ...(dash.palette && dash.palette !== DEFAULT_COLOR_THEME ? { palette: dash.palette } : {}),
        widgets: dash.widgets.map((w) => ({
          id: w.id,
          span: w.span,
          /* the arrangement travels with the board: the box is what a canvas
             is laid out by, and an export without it came back at the other
             end as whatever the grid rules happened to compute. `span` and
             `rows` ride along for a board opened by an older build. */
          ...(w.rows && w.rows !== 1 ? { rows: w.rows } : {}),
          ...(hasBox(w) ? { x: w.x, y: w.y, w: w.w, h: w.h } : {}),
          /* how the card is dressed travels with it too: a board arranged
             around two frameless cards comes back framed otherwise */
          ...(w.noFrame ? { noFrame: true } : {}),
          ...(w.noTitle ? { noTitle: true } : {}),
          ...(w.title ? { title: w.title } : {}),
        })),
      })),
  };
}

/**
 * "Modernization" next to an existing one becomes "Modernization 1", and next
 * to that "Modernization 2". A name that already ends in a number counts on
 * from there, so a second copy of "Modernization 1" is "Modernization 2"
 * rather than being renumbered from the bottom.
 */
export function uniqueName(name, existing) {
  const taken = new Set(existing.map((n) => String(n).trim().toLowerCase()));
  const wanted = String(name || "Dashboard").trim() || "Dashboard";
  if (!taken.has(wanted.toLowerCase())) return wanted;

  const numbered = wanted.match(/^(.*?)\s+(\d+)$/);
  const stem = numbered ? numbered[1] : wanted;
  const from = numbered ? Number(numbered[2]) + 1 : 1;

  for (let n = from; n < from + 1000; n += 1) {
    const candidate = `${stem} ${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return `${stem} ${Date.now()}`;
}

/* The size buttons offer four widths, but a widget also picks up whatever span
   it was stretched to when a row was filled — 5 and 8 are ordinary values in a
   saved layout, so an import clamps to the grid rather than to the presets. */
const clampSpan = (span, fallback) => {
  const n = Math.round(Number(span));
  return Number.isFinite(n) && n >= 1 && n <= 12 ? n : fallback;
};

/** Reads a file's text, rejecting anything that is not one of our exports. */
export function parseExport(text) {
  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    throw new Error("That file is not a dashboard export.");
  }

  if (data?.format !== EXPORT_FORMAT || !Array.isArray(data.dashboards)) {
    throw new Error("That file is not a dashboard export.");
  }

  /* Widgets this build does not have are dropped rather than failing the
     import: an older export should still bring over everything it can. */
  const dashboards = data.dashboards
    .map((dash) => ({
      name: String(dash?.name || "Dashboard").slice(0, 60),
      ...(readPalette(dash?.palette) ? { palette: readPalette(dash.palette) } : {}),
      widgets: (Array.isArray(dash?.widgets) ? dash.widgets : [])
        .filter((w) => WIDGETS.some((known) => known.id === w?.id))
        .map((w) => ({
          id: w.id,
          span: clampSpan(w.span, WIDGETS.find((k) => k.id === w.id).span),
          /* the sizing, read back within the bounds this build allows */
          ...(Number(w.rows) > 1 ? { rows: Math.min(6, Math.round(Number(w.rows))) } : {}),
          /* and the arrangement: where the card sat and how big it was. All
             four or none - a half-read box is worse than no box, because the
             board can lay a widget out from spans but not from a stray y. */
          ...readBox(w),
          ...(w.noFrame === true ? { noFrame: true } : {}),
          ...(w.noTitle === true ? { noTitle: true } : {}),
          ...(typeof w.title === "string" && w.title.trim()
            ? { title: w.title.trim().slice(0, 60) }
            : {}),
        })),
    }))
    .filter((dash) => dash.widgets.length);

  if (!dashboards.length) throw new Error("That export has no dashboards this build can read.");
  return dashboards;
}

/**
 * A widget's box as it comes off a file: across as a fraction of the board,
 * down in pixels. Anything missing, out of range or not a number and the whole
 * box is dropped, so the board falls back to laying that widget out itself
 * rather than placing it somewhere nonsensical.
 */
function readBox(w) {
  const x = Number(w?.x);
  const y = Number(w?.y);
  const width = Number(w?.w);
  const height = Number(w?.h);

  if (![x, y, width, height].every(Number.isFinite)) return {};
  if (width <= 0 || width > 1 || x < 0 || x > 1) return {};

  return {
    x: Math.min(Math.max(x, 0), 1),
    y: Math.max(0, Math.round(y)),
    w: Math.min(Math.max(width, 0.02), 1),
    h: Math.max(MIN_H_PX, Math.round(height)),
  };
}

/** Appends imported dashboards under free names and opens the first of them. */
export function withImported(config, incoming) {
  const dashboards = [...config.dashboards];
  const added = incoming.map((dash, i) => {
    const next = {
      id: `dashboard-${Date.now()}-${i}`,
      name: uniqueName(dash.name, dashboards.map((d) => d.name)),
      ...(dash.palette ? { palette: dash.palette } : {}),
      widgets: dash.widgets,
    };
    dashboards.push(next);
    return next;
  });

  return { ...config, dashboards, activeId: added[0]?.id || config.activeId };
}

/* ---------- palette ---------- */

/**
 * Colour is a property of the dashboard, not of the browser: a board built to
 * be handed round should look the same wherever it is opened, so the palette
 * travels in the layout and in the export beside it.
 */
export const DEFAULT_PALETTE = DEFAULT_COLOR_THEME;

export const paletteIds = () => Object.keys(COLOR_THEMES);

/** How a custom palette spreads out from the one colour that was picked. */
export const PALETTE_SCHEMES = [
  { id: "mono", label: "Monochrome", hues: [0] },
  { id: "analogous", label: "Analogous", hues: [-28, 0, 28] },
  { id: "complementary", label: "Complementary", hues: [0, 180] },
  { id: "triadic", label: "Triadic", hues: [0, 120, 240] },
  { id: "tetradic", label: "Tetradic", hues: [0, 90, 180, 270] },
];

export const DEFAULT_SCHEME = "analogous";

/**
 * Ten swatches from a single colour: each hue the scheme calls for, walked
 * from light to dark, so the result still reads as a ramp when the charts sort
 * it by luminance.
 */
export function buildPalette(base, scheme = DEFAULT_SCHEME) {
  let seed;
  try {
    seed = chroma(base || "#f6400d");
  } catch (e) {
    seed = chroma("#f6400d");
  }

  const hue = seed.get("hsl.h") || 0;
  // a washed-out pick would give ten near-greys, so saturation has a floor
  const sat = Math.min(Math.max(seed.get("hsl.s") || 0.6, 0.32), 0.95);
  const hues = (PALETTE_SCHEMES.find((s) => s.id === scheme) || PALETTE_SCHEMES[1]).hues;
  const steps = Math.ceil(10 / hues.length);

  const out = [];
  hues.forEach((offset) => {
    for (let i = 0; i < steps; i += 1) {
      const light = 0.74 - (0.48 * i) / Math.max(steps - 1, 1);
      out.push(chroma.hsl((hue + offset + 360) % 360, sat, light).hex());
    }
  });
  return out.slice(0, 10);
}

export const isCustomPalette = (value) => !!value && typeof value === "object" && !!value.base;

/* A palette that carries its own ten rather than deriving them from one colour
   and a scheme: taken off a picture, or edited swatch by swatch. The base is
   kept alongside them - it is what everything else treats as the accent - so
   every test above still answers the same way. */
const ownColors = (value) =>
  (Array.isArray(value?.colors) && value.colors.length ? value.colors : null);

/**
 * A dashboard stores either the id of a shared theme or its own {base, scheme}.
 * Both resolve to the same shape, so nothing downstream has to care which.
 */
export function paletteOf(value) {
  if (isCustomPalette(value)) {
    return {
      label: value.name?.trim() || "Custom",
      // the colour that was picked leads the palette, so it becomes the accent
      base: value.base,
      // ...its own ten where it has them, the scheme's otherwise
      nodePalette: ownColors(value) || buildPalette(value.base, value.scheme),
      edge: value.base,
      edgeHighlight: value.base,
    };
  }
  return COLOR_THEMES[value] || COLOR_THEMES[DEFAULT_COLOR_THEME];
}

/** Stable key for memoing on a palette, custom or not. */
export const paletteKey = (value) =>
  isCustomPalette(value)
    ? `custom:${value.base}:${value.scheme || DEFAULT_SCHEME}:${(ownColors(value) || []).join("")}`
    : value || DEFAULT_PALETTE;

const HEX = /^#[0-9a-f]{3,8}$/i;

/**
 * A palette off a file, kept only if this build can draw it: a shared theme by
 * id, or a custom one whose colour actually parses. Anything else is dropped
 * rather than failing the import, and the board comes in on the default.
 */
export function readPalette(value) {
  if (typeof value === "string") return COLOR_THEMES[value] ? value : null;
  if (!value || typeof value !== "object" || !HEX.test(String(value.base || ""))) return null;

  const scheme = PALETTE_SCHEMES.some((s) => s.id === value.scheme) ? value.scheme : DEFAULT_SCHEME;
  const name = typeof value.name === "string" ? value.name.trim().slice(0, 40) : "";
  return { base: value.base.toLowerCase(), scheme, ...(name ? { name } : {}) };
}
